import { useState, type FormEvent } from "react";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import { setAdvancedMatching } from "@/lib/metaPixel";

const SHOPIFY_STORE = "coyotecreekhatco.com";

const SOCIALS = [
  {
    name: "Instagram",
    href: "https://instagram.com/coyotecreekhatco",
    icon: Instagram,
  },
  {
    name: "TikTok",
    href: "https://tiktok.com/@coyotecreekhatco",
    // lucide-react has no TikTok icon — inline SVG
    icon: (props: React.SVGProps<SVGSVGElement>) => (
      <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.55a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.0z" />
      </svg>
    ),
  },
  {
    name: "Twitter",
    href: "https://twitter.com/coyotecreekco",
    // X / Twitter
    icon: (props: React.SVGProps<SVGSVGElement>) => (
      <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
];

const POLICY_LINKS = [
  { label: "Shipping & Handling", to: "/policies/shipping-policy" },
  { label: "Return & Refund", to: "/policies/refund-policy" },
  { label: "Terms of Service", to: "/policies/terms-of-service" },
  { label: "Privacy Policy", to: "/policies/privacy-policy" },
  { label: "Your Privacy Choices", to: "/policies/your-privacy-choices" },
];

const SUPPORT_LINKS = [
  { label: "FAQs", to: "/faqs" },
  { label: "Contact Us", to: "/contact" },
  { label: "Become a Dealer", to: "/wholesale" },
  { label: "Creator Creek", to: "/creator-creek" },
  { label: "Affiliate Login", href: "https://coyotecreekhatco.bixgrow.com/login", external: true },
];

const COMPANY_LINKS = [
  { label: "Heritage", to: "/heritage" },
  { label: "The Standard", to: "/the-standard" },
  { label: "Our Values", to: "/our-values" },
  { label: "Range Map", to: "/range-map" },
  { label: "Custom Hats", href: "https://gwestcustoms.com", external: true },
];

import { Link } from "@tanstack/react-router";
import cLogoOrange from "@/assets/c-logo-orange.svg";
import taglineImg from "@/assets/coyote-creek-tagline-v2.png";

export function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    // Basic email shape check
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setAdvancedMatching({ em: trimmed });
    setSubmitting(true);
    // No backend wired — acknowledge locally
    setTimeout(() => {
      toast.success("Thanks for joining the herd!", {
        description: "We'll keep you posted on drops and stories.",
      });
      setEmail("");
      setSubmitting(false);
    }, 400);
  };

  return (
    <footer style={{ backgroundColor: "#2A1510", color: "#F5EFE2" }}>
      {/* Top section: logo + socials + email */}
      <div className="mx-auto max-w-[1400px] px-6 pt-16 pb-12 text-center">
        <Link to="/" className="inline-flex flex-col items-center">
          <img
            src={cLogoOrange}
            alt=""
            aria-hidden="true"
            className="h-32 w-32 md:h-40 md:w-40"
          />
          <img
            src={taglineImg}
            alt="Coyote Creek — Premium Lifestyle Headwear"
            className="-mt-8 md:-mt-12 h-auto w-full max-w-[480px] md:max-w-[600px]"
          />
        </Link>

        {/* Socials */}
        <div className="-mt-4 md:-mt-8 flex items-center justify-center gap-5">
          {SOCIALS.map((social) => {
            const Icon = social.icon;
            return (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#F5EFE2]/40 text-[#F5EFE2]/80 transition-all hover:border-blaze hover:bg-blaze hover:text-blaze-foreground"
              >
                <Icon className="h-5 w-5" />
              </a>
            );
          })}
        </div>

        {/* Email signup */}
        <div className="mx-auto mt-12 max-w-md">
          <h3 className="font-ui text-xs font-bold uppercase tracking-[0.25em] text-[#F5EFE2]">
            Join the Coyote Creek Community
          </h3>
          <p className="mt-2 font-body text-sm text-[#F5EFE2]/70">
            New drops, stories from the field, and members-only offers.
          </p>
          <form onSubmit={handleSubscribe} className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              maxLength={255}
              aria-label="Email address"
              className="flex-1 rounded-md border border-[#F5EFE2]/40 bg-transparent px-4 py-3 text-sm text-[#F5EFE2] placeholder:text-[#F5EFE2]/50 focus:border-blaze focus:outline-none"
            />
            <button
              type="submit"
              disabled={submitting}
              className="btn-cc-subscribe tracking-[0.2em]"
            >
              {submitting ? "Joining…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      {/* Footer menu */}
      <div className="mx-auto max-w-[1100px] px-6 py-12">
        <div className="mx-auto grid gap-10 text-center md:grid-cols-3">
          <div>
            <h4 className="inline-block border-b-2 border-[#F5EFE2] pb-2 font-ui text-sm font-bold uppercase tracking-[0.25em] text-[#F5EFE2]">
              Customer Care
            </h4>
            <ul className="mt-5 space-y-3">
              {POLICY_LINKS.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    className="font-body text-sm text-[#F5EFE2]/70 transition-colors hover:text-blaze"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="inline-block border-b-2 border-[#F5EFE2] pb-2 font-ui text-sm font-bold uppercase tracking-[0.25em] text-[#F5EFE2]">
              Support
            </h4>
            <ul className="mt-5 space-y-3">
              {SUPPORT_LINKS.map((link) => (
                <li key={link.label}>
                  {"external" in link && link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-sm text-[#F5EFE2]/70 transition-colors hover:text-blaze"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.to!}
                      className="font-body text-sm text-[#F5EFE2]/70 transition-colors hover:text-blaze"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="inline-block border-b-2 border-[#F5EFE2] pb-2 font-ui text-sm font-bold uppercase tracking-[0.25em] text-[#F5EFE2]">
              Company
            </h4>
            <ul className="mt-5 space-y-3">
              {COMPANY_LINKS.map((link) => (
                <li key={link.label}>
                  {"external" in link && link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-sm text-[#F5EFE2]/70 transition-colors hover:text-blaze"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.to!}
                      className="font-body text-sm text-[#F5EFE2]/70 transition-colors hover:text-blaze"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
      {/* Bottom bar */}
      <div className="border-t border-[#F5EFE2]/20">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-3 px-6 py-6 md:flex-row">
          <p className="font-ui text-xs text-[#F5EFE2]/60">
            © {new Date().getFullYear()} Coyote Creek Hat Co. All rights reserved.
          </p>
          <p className="font-ui text-xs italic text-[#F5EFE2]/60">
            "PREMIUM SPORTING HEADWEAR"
          </p>
        </div>
      </div>
    </footer>
  );
}
